import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { PaginationParams } from '../../core/models/api.models';
import {
  CreateKanbanBoardRequest,
  KanbanBoardDto,
  KanbanBoardViewDto,
  KanbanColumnViewDto,
  KanbanIssueCardDto
} from './kanban.models';

@Injectable({ providedIn: 'root' })
export class KanbanApiService {
  private readonly api = inject(ApiService);
  private readonly base = 'v1/kanban';

  listBoards(projectId: string): Observable<KanbanBoardDto[]> {
    return this.api.get<KanbanBoardDto[]>(`${this.base}/projects/${projectId}/boards`);
  }

  getBoardView(boardId: string, params: PaginationParams = {}): Observable<KanbanBoardViewDto> {
    return this.api.get<KanbanBoardViewDto>(`${this.base}/boards/${boardId}`, params);
  }

  createBoard(request: CreateKanbanBoardRequest): Observable<KanbanBoardDto> {
    return this.api.post<KanbanBoardDto>(`${this.base}/boards`, request);
  }

  updateBoard(boardId: string, body: {
    name: string;
    description?: string;
    isDefault: boolean;
    swimlaneType: number;
    defaultSavedFilterId?: string | null;
  }): Observable<KanbanBoardDto> {
    return this.api.put<KanbanBoardDto>(`${this.base}/boards/${boardId}`, body);
  }

  deleteBoard(boardId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/boards/${boardId}`);
  }

  addColumn(boardId: string, body: {
    name: string;
    mappedIssueStatus: number;
    wipLimit?: number;
  }): Observable<KanbanColumnViewDto> {
    return this.api.post<KanbanColumnViewDto>(`${this.base}/boards/${boardId}/columns`, body);
  }

  updateColumn(boardId: string, columnId: string, body: {
    name: string;
    mappedIssueStatus: number;
    wipLimit?: number | null;
    sortOrder: number;
  }): Observable<KanbanColumnViewDto> {
    return this.api.put<KanbanColumnViewDto>(`${this.base}/boards/${boardId}/columns/${columnId}`, body);
  }

  moveIssue(boardId: string, issueId: string, targetColumnId: string, enforceWipLimit = true): Observable<KanbanIssueCardDto> {
    return this.api.post<KanbanIssueCardDto>(`${this.base}/boards/${boardId}/issues/${issueId}/move`, {
      targetColumnId,
      enforceWipLimit
    });
  }

  /** Bulk assign / unassign / status change via single board bulk endpoint. */
  bulkUpdate(boardId: string, body: {
    issueIds: string[];
    status?: number | null;
    assigneeId?: string | null;
    unassign?: boolean | null;
  }): Observable<KanbanIssueCardDto[]> {
    return this.api.post<KanbanIssueCardDto[]>(`${this.base}/boards/${boardId}/issues/bulk`, body);
  }

  bulkAssign(boardId: string, issueIds: string[], assigneeId: string | null): Observable<KanbanIssueCardDto[]> {
    if (!assigneeId) {
      return this.bulkUpdate(boardId, { issueIds, unassign: true });
    }
    return this.bulkUpdate(boardId, { issueIds, assigneeId });
  }

  /** Moves by setting each issue's status to the target column's mapped IssueStatus. */
  bulkMove(boardId: string, issueIds: string[], mappedIssueStatus: number): Observable<KanbanIssueCardDto[]> {
    return this.bulkUpdate(boardId, { issueIds, status: mappedIssueStatus });
  }
}
